/**
 * Apply Page - Comprehensive Loan Application Form
 * Legacy Triangle
 */

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { 
  User, 
  Building2, 
  DollarSign, 
  FileText,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Briefcase,
  CreditCard
} from "lucide-react";

const businessStructures = [
  "Sole Proprietorship",
  "Partnership",
  "LLC (Limited Liability Company)",
  "S-Corporation",
  "C-Corporation",
  "Non-Profit",
  "Other"
];

const loanPurposes = [
  "Working Capital",
  "Equipment Purchase",
  "Business Expansion",
  "Inventory Purchase",
  "Debt Refinancing",
  "Real Estate",
  "Marketing/Advertising",
  "Hiring/Payroll",
  "Other"
];

const loanTerms = [
  "6 months",
  "12 months",
  "24 months",
  "36 months",
  "48 months",
  "60 months",
  "Other"
];

// Helper function for step indicator styles
const getStepIndicatorStyles = (currentStep, stepNum) => {
  if (currentStep === stepNum) {
    return 'bg-[#e8821c] text-white';
  }
  if (currentStep > stepNum) {
    return 'bg-[#48d480] text-white';
  }
  return 'bg-white/10 text-white/50';
};

export default function Application() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    // Personal Information
    fullLegalName: "",
    dateOfBirth: "",
    homeAddress: "",
    personalPhone: "",
    personalEmail: "",
    
    // Business Information
    legalBusinessName: "",
    dba: "",
    businessAddress: "",
    businessPhone: "",
    businessEmail: "",
    businessWebsite: "",
    businessStartDate: "",
    businessIndustry: "",
    businessStructure: "",
    federalEIN: "",
    stateOfIncorporation: "",
    numberOfEmployees: "",
    ownershipDetails: "",
    
    // Loan Request Details
    loanAmountRequested: "",
    purposeOfLoan: "",
    preferredLoanTerm: "",
    useOfFunds: "",
    
    // Business Financial Information
    annualRevenueLastYear: "",
    annualRevenuePreviousYear: "",
    netProfitLastYear: "",
    netProfitPreviousYear: "",
    existingBusinessDebt: "",
    monthlyBusinessExpenses: "",
    businessBankBalances: "",
    collateralAvailable: "",
    
    // Owner Financial Information
    ownerCreditScore: "",
    ownerAnnualIncome: "",
    ownerNetWorth: "",
    
    // Declarations
    bankruptcyHistory: "",
    legalProceedings: "",
    taxLiensOrJudgments: "",
    previousLoanDenials: "",
    existingFederalDebt: "",
    
    // Consent
    termsAccepted: false,
    smsConsent: false
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.termsAccepted) {
      alert("Please accept the terms and conditions to continue.");
      return;
    }
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formData.fullLegalName.split(' ')[0] || formData.fullLegalName,
          lastName: formData.fullLegalName.split(' ').slice(1).join(' ') || '',
          email: formData.personalEmail,
          phone: formData.personalPhone,
          businessName: formData.legalBusinessName,
          consultationType: 'Full Loan Application',
          fundingRange: formData.loanAmountRequested,
          yearsInBusiness: formData.businessStartDate,
          message: `
LOAN APPLICATION SUBMISSION

=== PERSONAL INFORMATION ===
Full Legal Name: ${formData.fullLegalName}
Date of Birth: ${formData.dateOfBirth}
Home Address: ${formData.homeAddress}
Personal Phone: ${formData.personalPhone}
Personal Email: ${formData.personalEmail}

=== BUSINESS INFORMATION ===
Legal Business Name: ${formData.legalBusinessName}
DBA: ${formData.dba || 'N/A'}
Business Address: ${formData.businessAddress}
Business Phone: ${formData.businessPhone}
Business Email: ${formData.businessEmail}
Business Website: ${formData.businessWebsite || 'N/A'}
Business Start Date: ${formData.businessStartDate}
Business Industry: ${formData.businessIndustry}
Business Structure: ${formData.businessStructure}
Federal EIN: ${formData.federalEIN}
State of Incorporation: ${formData.stateOfIncorporation}
Number of Employees: ${formData.numberOfEmployees}
Ownership Details: ${formData.ownershipDetails}

=== LOAN REQUEST DETAILS ===
Loan Amount Requested: ${formData.loanAmountRequested}
Purpose of Loan: ${formData.purposeOfLoan}
Preferred Loan Term: ${formData.preferredLoanTerm}
Use of Funds: ${formData.useOfFunds}

=== BUSINESS FINANCIAL INFORMATION ===
Annual Revenue (Last Year): ${formData.annualRevenueLastYear}
Annual Revenue (Previous Year): ${formData.annualRevenuePreviousYear}
Net Profit (Last Year): ${formData.netProfitLastYear}
Net Profit (Previous Year): ${formData.netProfitPreviousYear}
Existing Business Debt: ${formData.existingBusinessDebt}
Monthly Business Expenses: ${formData.monthlyBusinessExpenses}
Business Bank Balances: ${formData.businessBankBalances}
Collateral Available: ${formData.collateralAvailable}

=== OWNER FINANCIAL INFORMATION ===
Owner Credit Score: ${formData.ownerCreditScore}
Owner Annual Income: ${formData.ownerAnnualIncome}
Owner Net Worth: ${formData.ownerNetWorth}

=== DECLARATIONS ===
Bankruptcy History: ${formData.bankruptcyHistory || 'None'}
Legal Proceedings: ${formData.legalProceedings || 'None'}
Tax Liens or Judgments: ${formData.taxLiensOrJudgments || 'None'}
Previous Loan Denials: ${formData.previousLoanDenials || 'None'}
Existing Federal Debt: ${formData.existingFederalDebt || 'None'}
          `.trim()
        })
      });
      
      if (response.ok) {
        setSubmitSuccess(true);
      }
    } catch {
      // Form submission failed silently
    }
    
    setIsSubmitting(false);
  };

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, 6));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const steps = [
    { num: 1, label: "Personal", icon: User },
    { num: 2, label: "Business", icon: Building2 },
    { num: 3, label: "Loan Details", icon: DollarSign },
    { num: 4, label: "Financials", icon: Briefcase },
    { num: 5, label: "Owner Info", icon: CreditCard },
    { num: 6, label: "Declarations", icon: FileText }
  ];

  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-[#0f2d52]">
        <Navbar />
        <div className="min-h-[80vh] flex items-center justify-center px-4 pt-24">
          <div className="bg-white rounded-2xl p-8 md:p-12 max-w-lg text-center">
            <div className="w-20 h-20 rounded-full bg-[#48d480]/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-[#48d480]" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0f2d52] mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Application Submitted!
            </h2>
            <p className="text-gray-600 mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
              Thank you for submitting your loan application. Our funding specialists will review your 
              information and contact you within 24-48 business hours.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-[#48d480] hover:bg-[#3bc070] text-white font-bold px-6 py-3 rounded-lg transition-colors"
            >
              Back to Home <ArrowRight size={18} />
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f2d52]" data-testid="application-page">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-28 pb-6 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Business Loan <span className="text-[#48d480]">Application</span>
          </h1>
          <p className="text-white/70" style={{ fontFamily: "'Inter', sans-serif" }}>
            Complete the form below to apply for funding. All information is kept confidential.
          </p>
        </div>
      </section>

      {/* Progress Steps */}
      <section className="pb-6 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between overflow-x-auto pb-2">
            {steps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <div key={step.num} className="flex items-center">
                  <div 
                    className={`flex flex-col items-center cursor-pointer ${idx > 0 ? 'ml-2' : ''}`}
                    onClick={() => setCurrentStep(step.num)}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${getStepIndicatorStyles(currentStep, step.num)}`}>
                      {currentStep > step.num ? <CheckCircle size={20} /> : <Icon size={18} />}
                    </div>
                    <span className={`text-xs mt-1 whitespace-nowrap ${
                      currentStep === step.num ? 'text-[#e8821c]' : 'text-white/50'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                  {idx < steps.length - 1 && (
                    <div className={`w-8 md:w-16 h-0.5 mx-1 ${
                      currentStep > step.num ? 'bg-[#48d480]' : 'bg-white/20'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSubmit}>
            <div className="bg-[#1a3d5c] rounded-2xl p-6 md:p-8 border border-white/10">
              
              {/* Step 1: Personal Information */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-[#e8821c]/20 flex items-center justify-center">
                      <User size={20} className="text-[#e8821c]" />
                    </div>
                    <h2 className="text-xl font-bold text-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      Personal Information
                    </h2>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Full Legal Name <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="text"
                        name="fullLegalName"
                        required
                        value={formData.fullLegalName}
                        onChange={handleChange}
                        placeholder="John Michael Smith"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Date of Birth <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="date"
                        name="dateOfBirth"
                        required
                        value={formData.dateOfBirth}
                        onChange={handleChange}
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Personal Phone Number <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="tel"
                        name="personalPhone"
                        required
                        value={formData.personalPhone}
                        onChange={handleChange}
                        placeholder="(555) 123-4567"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Home Address <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="text"
                        name="homeAddress"
                        required
                        value={formData.homeAddress}
                        onChange={handleChange}
                        placeholder="123 Main St, City, State ZIP"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Personal Email Address <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="email"
                        name="personalEmail"
                        required
                        value={formData.personalEmail}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Business Information */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-[#e8821c]/20 flex items-center justify-center">
                      <Building2 size={20} className="text-[#e8821c]" />
                    </div>
                    <h2 className="text-xl font-bold text-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      Business Information
                    </h2>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Legal Business Name <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="text"
                        name="legalBusinessName"
                        required
                        value={formData.legalBusinessName}
                        onChange={handleChange}
                        placeholder="ABC Company LLC"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        DBA (Doing Business As)
                      </label>
                      <input
                        type="text"
                        name="dba"
                        value={formData.dba}
                        onChange={handleChange}
                        placeholder="If different from legal name"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Business Address <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="text"
                        name="businessAddress"
                        required
                        value={formData.businessAddress}
                        onChange={handleChange}
                        placeholder="456 Business Ave, Suite 100, City, State ZIP"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Business Phone Number <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="tel"
                        name="businessPhone"
                        required
                        value={formData.businessPhone}
                        onChange={handleChange}
                        placeholder="(555) 987-6543"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Business Email Address <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="email"
                        name="businessEmail"
                        required
                        value={formData.businessEmail}
                        onChange={handleChange}
                        placeholder="info@company.com"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Business Website
                      </label>
                      <input
                        type="url"
                        name="businessWebsite"
                        value={formData.businessWebsite}
                        onChange={handleChange}
                        placeholder="https://www.company.com"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Business Start Date <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="date"
                        name="businessStartDate"
                        required
                        value={formData.businessStartDate}
                        onChange={handleChange}
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Business Industry <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="text"
                        name="businessIndustry"
                        required
                        value={formData.businessIndustry}
                        onChange={handleChange}
                        placeholder="e.g., Construction, Retail, Healthcare"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Business Structure <span className="text-[#e8821c]">*</span>
                      </label>
                      <select
                        name="businessStructure"
                        required
                        value={formData.businessStructure}
                        onChange={handleChange}
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#48d480] transition-colors"
                      >
                        <option value="">Select structure...</option>
                        {businessStructures.map(structure => (
                          <option key={structure} value={structure}>{structure}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Federal EIN / Tax ID <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="text"
                        name="federalEIN"
                        required
                        value={formData.federalEIN}
                        onChange={handleChange}
                        placeholder="XX-XXXXXXX"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        State of Incorporation <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="text"
                        name="stateOfIncorporation"
                        required
                        value={formData.stateOfIncorporation}
                        onChange={handleChange}
                        placeholder="e.g., North Carolina"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Number of Employees <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="number"
                        name="numberOfEmployees"
                        required
                        value={formData.numberOfEmployees}
                        onChange={handleChange}
                        placeholder="e.g., 5"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Ownership Details <span className="text-[#e8821c]">*</span>
                      </label>
                      <textarea
                        name="ownershipDetails"
                        required
                        value={formData.ownershipDetails}
                        onChange={handleChange}
                        placeholder="List all owners with their ownership percentages (e.g., John Smith - 60%, Jane Doe - 40%)"
                        rows={3}
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors resize-none"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Loan Request Details */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-[#e8821c]/20 flex items-center justify-center">
                      <DollarSign size={20} className="text-[#e8821c]" />
                    </div>
                    <h2 className="text-xl font-bold text-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      Loan Request Details
                    </h2>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Loan Amount Requested <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="text"
                        name="loanAmountRequested"
                        required
                        value={formData.loanAmountRequested}
                        onChange={handleChange}
                        placeholder="$50,000"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Purpose of Loan <span className="text-[#e8821c]">*</span>
                      </label>
                      <select
                        name="purposeOfLoan"
                        required
                        value={formData.purposeOfLoan}
                        onChange={handleChange}
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#48d480] transition-colors"
                      >
                        <option value="">Select purpose...</option>
                        {loanPurposes.map(purpose => (
                          <option key={purpose} value={purpose}>{purpose}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Preferred Loan Term <span className="text-[#e8821c]">*</span>
                      </label>
                      <select
                        name="preferredLoanTerm"
                        required
                        value={formData.preferredLoanTerm}
                        onChange={handleChange}
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#48d480] transition-colors"
                      >
                        <option value="">Select term...</option>
                        {loanTerms.map(term => (
                          <option key={term} value={term}>{term}</option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Use of Funds <span className="text-[#e8821c]">*</span>
                      </label>
                      <textarea
                        name="useOfFunds"
                        required
                        value={formData.useOfFunds}
                        onChange={handleChange}
                        placeholder="Describe how you plan to use the funds (e.g., Covering overhead, Cash flow between projects, Equipment purchase, etc.)"
                        rows={4}
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors resize-none"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Business Financial Information */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-[#e8821c]/20 flex items-center justify-center">
                      <Briefcase size={20} className="text-[#e8821c]" />
                    </div>
                    <h2 className="text-xl font-bold text-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      Business Financial Information
                    </h2>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Annual Gross Revenue (Last Year) <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="text"
                        name="annualRevenueLastYear"
                        required
                        value={formData.annualRevenueLastYear}
                        onChange={handleChange}
                        placeholder="$250,000"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Annual Gross Revenue (Previous Year) <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="text"
                        name="annualRevenuePreviousYear"
                        required
                        value={formData.annualRevenuePreviousYear}
                        onChange={handleChange}
                        placeholder="$200,000"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Net Profit (Last Year) <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="text"
                        name="netProfitLastYear"
                        required
                        value={formData.netProfitLastYear}
                        onChange={handleChange}
                        placeholder="$75,000"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Net Profit (Previous Year) <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="text"
                        name="netProfitPreviousYear"
                        required
                        value={formData.netProfitPreviousYear}
                        onChange={handleChange}
                        placeholder="$60,000"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Existing Business Debt <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="text"
                        name="existingBusinessDebt"
                        required
                        value={formData.existingBusinessDebt}
                        onChange={handleChange}
                        placeholder="$0 or amount owed"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Monthly Business Expenses <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="text"
                        name="monthlyBusinessExpenses"
                        required
                        value={formData.monthlyBusinessExpenses}
                        onChange={handleChange}
                        placeholder="$15,000"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Business Bank Account Balances <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="text"
                        name="businessBankBalances"
                        required
                        value={formData.businessBankBalances}
                        onChange={handleChange}
                        placeholder="$25,000"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Collateral Available
                      </label>
                      <input
                        type="text"
                        name="collateralAvailable"
                        value={formData.collateralAvailable}
                        onChange={handleChange}
                        placeholder="e.g., Work van, tools, equipment, real estate"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 5: Owner Financial Information */}
              {currentStep === 5 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-[#e8821c]/20 flex items-center justify-center">
                      <CreditCard size={20} className="text-[#e8821c]" />
                    </div>
                    <h2 className="text-xl font-bold text-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      Owner Financial Information
                    </h2>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Owner Credit Score <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="text"
                        name="ownerCreditScore"
                        required
                        value={formData.ownerCreditScore}
                        onChange={handleChange}
                        placeholder="e.g., 720"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Owner Annual Income <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="text"
                        name="ownerAnnualIncome"
                        required
                        value={formData.ownerAnnualIncome}
                        onChange={handleChange}
                        placeholder="$85,000"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Owner Net Worth <span className="text-[#e8821c]">*</span>
                      </label>
                      <input
                        type="text"
                        name="ownerNetWorth"
                        required
                        value={formData.ownerNetWorth}
                        onChange={handleChange}
                        placeholder="$150,000"
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 6: Declarations */}
              {currentStep === 6 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-[#e8821c]/20 flex items-center justify-center">
                      <FileText size={20} className="text-[#e8821c]" />
                    </div>
                    <h2 className="text-xl font-bold text-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      Declarations
                    </h2>
                  </div>

                  <p className="text-white/60 text-sm mb-4">
                    Please answer the following questions honestly. Providing false information may result in denial of your application.
                  </p>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Bankruptcy History
                      </label>
                      <textarea
                        name="bankruptcyHistory"
                        value={formData.bankruptcyHistory}
                        onChange={handleChange}
                        placeholder="Have you or your business filed for bankruptcy in the last 7 years? If yes, provide details."
                        rows={2}
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors resize-none"
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Legal Proceedings
                      </label>
                      <textarea
                        name="legalProceedings"
                        value={formData.legalProceedings}
                        onChange={handleChange}
                        placeholder="Are you currently involved in any legal proceedings? If yes, provide details."
                        rows={2}
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors resize-none"
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Tax Liens or Judgments
                      </label>
                      <textarea
                        name="taxLiensOrJudgments"
                        value={formData.taxLiensOrJudgments}
                        onChange={handleChange}
                        placeholder="Do you have any outstanding tax liens or judgments? If yes, provide details."
                        rows={2}
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors resize-none"
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Previous Loan Denials
                      </label>
                      <textarea
                        name="previousLoanDenials"
                        value={formData.previousLoanDenials}
                        onChange={handleChange}
                        placeholder="Have you been denied a business loan in the past 12 months? If yes, provide details."
                        rows={2}
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors resize-none"
                      />
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Existing Federal Debt
                      </label>
                      <textarea
                        name="existingFederalDebt"
                        value={formData.existingFederalDebt}
                        onChange={handleChange}
                        placeholder="Do you owe any federal debt (e.g., student loans, SBA loans)? If yes, provide details."
                        rows={2}
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-[#48d480] transition-colors resize-none"
                      />
                    </div>

                    {/* SMS Consent */}
                    <div className="bg-[#0f2d52] rounded-lg p-4 border border-white/10 mt-6">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id="smsConsent"
                          name="smsConsent"
                          checked={formData.smsConsent}
                          onChange={handleChange}
                          className="mt-1 w-5 h-5 rounded border-white/20 bg-[#0f2d52] text-[#48d480] focus:ring-[#48d480]"
                        />
                        <label htmlFor="smsConsent" className="text-white/70 text-sm">
                          I agree to receive SMS/text messages regarding my funding inquiry. 
                          Message frequency may vary. Message and data rates may apply. 
                          Reply STOP to opt out or HELP for assistance.
                        </label>
                      </div>
                    </div>

                    {/* Terms Acceptance */}
                    <div className="bg-[#0f2d52] rounded-lg p-4 border border-white/10 mt-4">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id="termsAccepted"
                          name="termsAccepted"
                          checked={formData.termsAccepted}
                          onChange={handleChange}
                          className="mt-1 w-5 h-5 rounded border-white/20 bg-[#0f2d52] text-[#48d480] focus:ring-[#48d480]"
                        />
                        <label htmlFor="termsAccepted" className="text-white/70 text-sm">
                          I certify that all information provided in this application is true and accurate to the best of my knowledge. 
                          I authorize Legacy Triangle to verify all information and obtain credit reports as necessary. 
                          I have read and agree to the <Link to="/terms-of-service" className="text-[#48d480] hover:underline">Terms of Service</Link> and 
                          <Link to="/privacy-policy" className="text-[#48d480] hover:underline ml-1">Privacy Policy</Link>.
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
                {currentStep > 1 ? (
                  <button
                    type="button"
                    onClick={prevStep}
                    className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
                  >
                    <ArrowLeft size={18} /> Previous
                  </button>
                ) : (
                  <div />
                )}

                {currentStep < 6 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="flex items-center gap-2 bg-[#48d480] hover:bg-[#3bc070] text-[#0f2d52] font-bold py-3 px-6 rounded-lg transition-colors"
                  >
                    Continue <ArrowRight size={18} />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting || !formData.termsAccepted}
                    className="flex items-center gap-2 bg-[#e8821c] hover:bg-[#d67518] text-white font-bold py-3 px-8 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {isSubmitting ? "Submitting..." : "Submit Application"} <ArrowRight size={18} />
                  </button>
                )}
              </div>

            </div>
          </form>

          {/* Progress Info */}
          <p className="text-center text-white/40 text-sm mt-4">
            Step {currentStep} of 6 • Your information is encrypted and secure
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
